/**
 * Class movement abilities — single knob file for the four picks from
 * docs/plans/2026-10-07-fun-and-polish/04a-MOVEMENT-ABILITY-CONCEPTS.md
 * (DJ decision D13, 2026-10-07). Mana and cooldown live in TOML
 * (content/abilities/<class>.toml); everything that shapes the MOVE lives
 * here so feel tuning is one file.
 *
 * Server limits these must respect (04a §1.3): a dash is horizontal only,
 * `distance` ≤ 500 px and `travel_ms` ≤ 2000 (dashSchema).
 */
export const CLASS_MOVEMENT_CONFIG = {
  /** Shaman — a straight-line lightning dash, gravity off, works mid-air. */
  stormstride: {
    distancePx: 350,
    /** 350 px / 150 ms ≈ 2330 px/s — about twice Charge. */
    travelMs: 150,
  },
  /** Druid — each press is one springing leap; up to three in a chain
   *  (the chain rule is RECAST_CHAINS.harebound in src/shared/combat/recastChains.ts). */
  harebound: {
    distancePx: 280,
    /** Leap arc: apex ≈ g·T²/8 ≈ 67 px; ≈ 470 px/s forward. */
    travelMs: 600,
    /** Carry this bound's speed under normal gravity until landing/recast. */
    horizontalPolicy: 'until-landed',
  },
  /** Warlock — an instant step through a door, forward only (return later). */
  pactDoor: {
    distancePx: 350,
  },
  /** Rogue — a near-instant shadow step forward, preserving held movement momentum. */
  shadowStep: {
    distancePx: 200,
    /** 200 px / 100 ms ≈ 2000 px/s — quick shadow dash that hands back smoothly to held input. */
    travelMs: 100,
  },
  /** Priest — a soft lift, then a steerable float (FloatState). */
  lightfall: {
    /** Upward launch (px/s). The rise is the ordinary arc: v²/2g ≈ 200 px at
     *  the priest's gravity (1500 × 0.9). */
    liftVy: 735,
    /** Longest the float lasts, from the cast (ms). Landing ends it sooner. */
    floatMs: 2500,
    /** Gravity multiplier on the way down. */
    gravityScale: 0.3,
    /** Fall-speed cap while floating (px/s). */
    maxFallSpeed: 150,
    /** Added to speedMultiplier while floating (+15% air speed). */
    airSpeedBonus: 0.15,
  },
  /** Hunter — loose a line ahead and up, then reel along it (D22,
   *  2026-10-08): a straight climb forward, steeper than any leap, then a
   *  small release pop with free air control. From a step back it carries
   *  the hunter over the lip of the next arena tier (192 px up); against a
   *  tier's face it climbs the face. */
  tetherline: {
    /** Forward along the line (px). */
    distancePx: 280,
    /** Up along the line (px): over a 192 px tier with room to spare. */
    risePx: 220,
    /** Reel time (ms): ≈ 930 px/s forward, 730 px/s up. */
    travelMs: 300,
    /** Vertical velocity at release (px/s, up): ≈ 25 px more at the hunter's gravity. */
    releaseVy: -260,
  },
  /** Paladin — Divine Steed's gallop (D23, 2026-10-08): a ground run that
   *  follows the floor and falls off ledges, then the TOML's run-speed buff
   *  (duration_ms) on arrival. */
  divineSteed: {
    distancePx: 400,
    /** ≈ 1000 px/s. */
    travelMs: 400,
    /** Added to speedMultiplier for the buff: paladin 260 → 390 px/s, under
     *  the server's 500 px/s claim cap. */
    speedBonus: 0.5,
  },
  /** Mage Slow Fall (plan 21 B2) — Lightfall's float without the lift: every
   *  descent is cushioned until the next landing. How long one cast lasts at
   *  most is the TOML `duration_ms` (content/abilities/mage.toml). */
  slowFall: {
    /** Gravity multiplier on the way down. */
    gravityScale: 0.25,
    /** Fall-speed cap (px/s) — slower than Lightfall's drift. */
    maxFallSpeed: 120,
    /**
     * Horizontal deceleration (px/s²) with no input while floating. Plan 21
     * B24: with only the normal air drag the walking momentum carried the
     * whole 3.4 s fall (~300 px of glide, landing a tier lower than a plain
     * drop). Holding a direction still steers at full speed. 0 restores the
     * glide — DJ's call.
     */
    airDeceleration: 900,
  },
} as const;
